import { IdMap, RArray, RMap, RMutableMap, RSet } from "../collections";
import { toPairs } from "../core";
import { makeImmutable } from "../core/makeImmutable";
import { Menu } from "../ordering/Menu";
import { ModifierItemTypeId } from "../ordering/ModifierItemType";
import { Money } from "../ordering/Money";
import { PackagingContainerType, PackagingContainerTypeId, } from "../ordering/PackagingContainerType";
import { ProductTypeId } from "../ordering/ProductType";
import { Schedule, ScheduleId } from "../ordering/Schedule";
import { WeeklySchedule } from "../ordering/WeeklySchedule";
import { ProductTypeParser } from "./ProductTypeParser";
import { ParametrizationParser } from "./ParametrizationParser";
import { ModifierSettingsParser } from "./ModifierSettingsParser";
import { AvailabilitySpecParser } from "./AvailabilitySpecParser";
import { VolumeParser } from "./VolumeParser";
import { ModifierTypeParser } from "./ModifierTypeParser";
import { ModifierItemTypeParser } from "./ModifierItemTypeParser";
import { ProductCategoryParser } from "./ProductCategoryParser";
import { HalvingPolicyParser } from "./HalvingPolicyParser";
import { MenuArrangmentParser } from "./MenuArrangmentParser";
import { MenuSectionParser } from "./MenuSectionParser";
import { CustomParameterTypeParser } from "./CustomParameterTypeParser";
import { CrossSellingListId, Parametrization, PreorderSettings, ProductInstanceParser, ProductTypeGroup, ProductTypeGroupId, } from "../ordering";
import { CrossSellingListParser } from "./CrossSellingListParser";
/**
 * Transforms MenuV2 from backend to restaumatic-client objects.
 */
export class MenuV2Parser {
    constructor(params) {
        this.catalogue = params.catalogue;
        this.appOrigin = params.appOrigin;
        this.onlineOrderingSettings = params.onlineOrderingSettings;
        makeImmutable(this);
    }
    parseMenu() {
        const customParameterTypeParser = new CustomParameterTypeParser();
        const customParameterTypes = this.parseCustomParameterTypes(customParameterTypeParser);
        const parametrizationParser = new ParametrizationParser({
            customParameterTypes,
        });
        const availabilitySpecParser = new AvailabilitySpecParser({
            parametrizationParser,
        });
        // NOTICE fix for dependency loop
        customParameterTypeParser.setCustomParameterChoiceAvailabilitySpecs(availabilitySpecParser);
        const modifierSettingsParser = new ModifierSettingsParser({
            parametrizationParser,
        });
        const modifierItemTypeParser = new ModifierItemTypeParser({
            parametrizationParser,
            availabilitySpecParser,
        });
        const modifierTypeParser = new ModifierTypeParser({
            parametrizationParser,
            modifierItemTypeParser,
        });
        const modifierTypes = this.parseModifierTypes(modifierTypeParser);
        const halvingPolicyParser = new HalvingPolicyParser({
            parametrizationParser,
        });
        // NOTICE Fix for dependecy cycle between products and lists
        const crossSellingListIds = this.parseCrossSellingListIds();
        const productCategoryParser = new ProductCategoryParser({
            halvingPolicyParser,
            modifierTypes,
            crossSellingListIds,
        });
        const schedules = this.parseSchedules();
        const productCategories = this.parseProductCategories(productCategoryParser);
        const productTypeGroups = this.parseProductTypeGroups();
        const productTypeParser = new ProductTypeParser({
            parametrizationParser,
            modifierSettingsParser,
            availabilitySpecParser,
            productCategories,
            modifierTypes,
            schedules,
            productTypeGroups,
            crossSellingListIds,
        });
        const productTypes = this.parseProductTypes(productTypeParser);
        const menuSectionParser = new MenuSectionParser({
            productTypes,
            customParameterTypes,
        });
        const menuSections = this.parseMenuSections(menuSectionParser);
        const menuArrangmentParser = new MenuArrangmentParser({ menuSections });
        const productInstanceParser = new ProductInstanceParser({
            productCategories,
            productTypes,
            modifierTypes,
            customParameterTypes,
        });
        const crossSellingListParser = new CrossSellingListParser(productInstanceParser);
        return new Menu({
            productCategories,
            productTypes,
            modifierTypes,
            customParameterTypes,
            schedules,
            arrangments: this.parseMenuArragments(menuArrangmentParser),
            crossSellingLists: this.parseCrossSellingLists(crossSellingListIds, crossSellingListParser),
        });
    }
    parseProductTypeGroups() {
        var _a;
        const productTypeGroups = RMutableMap.empty();
        for (const [sectionId, section] of toPairs(this.catalogue.sections)) {
            if (section.content.tag !== "Products" ||
                !section.visibleIn.some((visbleIn) => visbleIn === this.appOrigin)) {
                continue;
            }
            const productTypeGroup = new ProductTypeGroup({
                id: new ProductTypeGroupId(sectionId),
                preorderSettings: new PreorderSettings({
                    preorderSettings: Skubacz.configuration.feature_flags.preorder
                        ? (_a = section.preorderSettings) !== null && _a !== void 0 ? _a : this.onlineOrderingSettings.defaultPreorderSettings
                        : this.onlineOrderingSettings.defaultPreorderSettings,
                }),
                availabilitySchedule: Parametrization.empty(true),
            });
            for (const [, item] of section.content.items) {
                if (item.tag === "SingleProduct") {
                    const productTypeId = new ProductTypeId(item.itemProduct);
                    productTypeGroups
                        .getOrCreate(productTypeId, () => [])
                        .push(productTypeGroup);
                }
            }
        }
        return RMap.fromIterable(productTypeGroups.entries.map(([productTypeId, productTypeGroups]) => [productTypeId, IdMap.fromIterable(productTypeGroups)]));
    }
    parseProductTypes(productTypeParser) {
        return IdMap.fromIterable(new RArray(toPairs(this.catalogue.products)).mapOptional(([id, productType]) => {
            if (!productType.visibleIn.some((visbleIn) => visbleIn === this.appOrigin)) {
                return null;
            }
            return productTypeParser.parse(id, productType);
        }));
    }
    parseMenuArragments(menuArrangmentParser) {
        return IdMap.fromIterable(toPairs(this.catalogue.menus).map(([arragmentId, arragment]) => menuArrangmentParser.parse(arragmentId, arragment)));
    }
    parsePackagingContainers() {
        const containers = RMap.fromIterable(toPairs(this.catalogue.packagingContainers).map(([id, container]) => [
            new PackagingContainerTypeId(id),
            {
                price: Money.fromBackend(container.price),
                capacity: VolumeParser.parse(container.capacity),
                allowedProductTypes: [],
                allowedModifierItemTypes: [],
            },
        ]));
        toPairs(this.catalogue.products).forEach(([id, product]) => {
            var _a, _b, _c, _d;
            const productId = new ProductTypeId(id);
            const productCategory = this.catalogue.categories[product.category];
            const categoryContainers = (_b = (_a = productCategory.packagingContainersConfig) === null || _a === void 0 ? void 0 : _a.allowedContainers) !== null && _b !== void 0 ? _b : [];
            const productContainers = (_d = (_c = product.packagingContainersConfigOverride) === null || _c === void 0 ? void 0 : _c.allowedContainers) !== null && _d !== void 0 ? _d : [];
            [...categoryContainers, ...productContainers].forEach((containerId) => {
                const allowedProductTypes = containers.rawGet(containerId).allowedProductTypes;
                if (!allowedProductTypes.includes(productId))
                    allowedProductTypes.push(productId);
            });
        });
        toPairs(this.catalogue.modifiers).forEach(([, modifier]) => {
            modifier.sections.forEach((section) => {
                section.items.forEach(([id, modifierItemType]) => {
                    var _a, _b;
                    const modifierItemTypeId = new ModifierItemTypeId(id);
                    const modifierItemContainers = (_b = (_a = modifierItemType.packagingContainersConfig) === null || _a === void 0 ? void 0 : _a.allowedContainers) !== null && _b !== void 0 ? _b : [];
                    modifierItemContainers.forEach((containerId) => {
                        containers
                            .rawGet(containerId)
                            .allowedModifierItemTypes.push(modifierItemTypeId);
                    });
                });
            });
        });
        return IdMap.fromIterable(containers.entries.map(([id, params]) => new PackagingContainerType({
            id,
            ...params,
            allowedProductTypes: RSet.fromIterable(params.allowedProductTypes),
            allowedModifierItemTypes: RSet.fromIterable(params.allowedModifierItemTypes),
        })));
    }
    parseModifierTypes(modifierTypeParser) {
        return IdMap.fromIterable(toPairs(this.catalogue.modifiers).map(([id, modifier]) => modifierTypeParser.parse(id, modifier)));
    }
    parseSchedules() {
        const schedules = toPairs(this.catalogue.availabilitySchedules).map(([id, schedule]) => new Schedule({
            id: new ScheduleId(id),
            schedule: WeeklySchedule.fromWeeklyAvailability(schedule.availability),
        }));
        return IdMap.fromIterable(schedules);
    }
    parseProductCategories(productCategoryParser) {
        return IdMap.fromIterable(toPairs(this.catalogue.categories).map(([id, category]) => productCategoryParser.parse(id, category)));
    }
    parseCustomParameterTypes(customParameterTypeParser) {
        return IdMap.fromIterable(toPairs(this.catalogue.customParameters).map(([id, customParameter]) => customParameterTypeParser.parse(id, customParameter)));
    }
    parseMenuSections(menuSectionParser) {
        const sections = toPairs(this.catalogue.sections).mapOptional(([sectionId, section]) => {
            if (!section.visibleIn.some((visbleIn) => visbleIn === this.appOrigin)) {
                return null;
            }
            return menuSectionParser.parse(sectionId, section);
        });
        return IdMap.fromIterable(sections);
    }
    parseCrossSellingListIds() {
        // NOTICE All filtering / disabling must be done here
        const crossSellingListIds = toPairs(this.catalogue.crossSellingListsV2).mapOptional(([rawCrossSellingListId, crossSellingList]) => {
            // TODO Filter visibility of lists
            return new CrossSellingListId(rawCrossSellingListId);
        });
        return RSet.fromIterable(crossSellingListIds);
    }
    parseCrossSellingLists(crossSellingListIds, crossSellingListParser) {
        const crossSellingLists = toPairs(this.catalogue.crossSellingListsV2).mapOptional(([rawCrossSellingListId, crossSellingList]) => {
            const crossSellingListId = crossSellingListIds.rawFind(rawCrossSellingListId);
            if (crossSellingListId === null) {
                return null;
            }
            return crossSellingListParser.parse(crossSellingListId, crossSellingList);
        });
        return IdMap.fromIterable(crossSellingLists);
    }
}
